import { da } from 'date-fns/locale'
import { format, formatDistanceToNowStrict, isValid, parseISO } from 'date-fns'

/**
 * Formatering til dansk.
 *
 * Grundregel: alle tidspunkter kommer fra API'et som UTC (ISO 8601 med offset)
 * og formateres i brugerens lokale tidszone her. Vi konverterer aldrig den
 * anden vej i UI-laget.
 */

const LOCALE = 'da-DK'

function toDate(value: Date | string): Date {
  return typeof value === 'string' ? parseISO(value) : value
}

/** Fx "6. aug. 2026". */
export function formatDate(value: Date | string): string {
  const date = toDate(value)
  return isValid(date) ? format(date, 'd. MMM yyyy', { locale: da }) : '—'
}

/** Fx "6. aug. 2026 kl. 14.30". */
export function formatDateTime(value: Date | string): string {
  const date = toDate(value)
  return isValid(date) ? format(date, "d. MMM yyyy 'kl.' HH.mm", { locale: da }) : '—'
}

/**
 * Fx "2 min siden". Bruges i aktivitetsfeeds, hvor det præcise klokkeslæt
 * kun er relevant på hover.
 */
export function formatRelativeTime(value: Date | string): string {
  const date = toDate(value)
  if (!isValid(date)) return '—'

  const distance = formatDistanceToNowStrict(date, { locale: da })
  return date.getTime() > Date.now() ? `om ${distance}` : `${distance} siden`
}

/** Heltal med dansk tusindtalsseparator: 1248 → "1.248". */
export function formatNumber(value: number, options?: Intl.NumberFormatOptions): string {
  return new Intl.NumberFormat(LOCALE, options).format(value)
}

/** Procent uden decimaler: 68 → "68 %". */
export function formatPercent(value: number, fractionDigits = 0): string {
  return `${formatNumber(value, {
    minimumFractionDigits: fractionDigits,
    maximumFractionDigits: fractionDigits,
  })} %`
}

/**
 * Beløb i kort form, som det vises i KPI-kort: 12_400_000 → "12,4 mio. kr.".
 * Fulde beløb bruges i tabeller, hvor præcisionen betyder noget.
 */
export function formatCurrencyCompact(value: number, currency = 'DKK'): string {
  const suffix = currency === 'DKK' ? 'kr.' : currency

  if (Math.abs(value) >= 1_000_000_000) {
    return `${formatNumber(value / 1_000_000_000, { maximumFractionDigits: 1 })} mia. ${suffix}`
  }
  if (Math.abs(value) >= 1_000_000) {
    return `${formatNumber(value / 1_000_000, { maximumFractionDigits: 1 })} mio. ${suffix}`
  }
  if (Math.abs(value) >= 1_000) {
    return `${formatNumber(value / 1_000, { maximumFractionDigits: 0 })} t. ${suffix}`
  }
  return `${formatNumber(value)} ${suffix}`
}

export function formatCurrency(value: number, currency = 'DKK'): string {
  return new Intl.NumberFormat(LOCALE, {
    style: 'currency',
    currency,
    maximumFractionDigits: 0,
  }).format(value)
}

/** Filstørrelse i binære enheder: 2_411_724 → "2,3 MB". */
export function formatFileSize(bytes: number): string {
  if (bytes <= 0) return '0 B'

  const units = ['B', 'KB', 'MB', 'GB', 'TB'] as const
  const exponent = Math.min(Math.floor(Math.log(bytes) / Math.log(1024)), units.length - 1)
  const value = bytes / 1024 ** exponent

  return `${formatNumber(value, { maximumFractionDigits: exponent === 0 ? 0 : 1 })} ${units[exponent]}`
}

/**
 * Tal med ental eller flertal: `formatCount(1, 'ny kunde', 'nye kunder')` →
 * "1 ny kunde".
 *
 * Dansk bøjer både navneordet og tillægsordet, så en løsning med et påhæftet
 * "(er)" bliver forkert oftere end den bliver rigtig. Begge former skrives
 * derfor ud af kalderen.
 */
export function formatCount(count: number, singular: string, plural: string): string {
  return `${formatNumber(count)} ${count === 1 ? singular : plural}`
}

/**
 * Konverterer et tal skrevet i dansk format til noget, API'et forstår.
 * "12.400.000,50" og "12400000.50" skal begge virke.
 *
 * Tvetydigheden sidder i punktummet: dansk bruger det som tusindtalsseparator,
 * men mange taster decimaler med punktum, fordi det numeriske tastatur gør det.
 * Med et komma i strengen er sagen klar — kommaet er decimalen, punktummerne
 * er tusinder. Uden komma afgør cifrene efter det sidste punktum det: 1-2
 * cifre kan kun være decimaler ("9.5"), tre er en tusindgruppe ("1.234").
 */
export function parseDanishNumber(value: string | undefined): number | null {
  if (!value || value.trim() === '') return null

  let normalised = value.replace(/\s/g, '')

  if (normalised.includes(',')) {
    normalised = normalised.replace(/\./g, '').replace(',', '.')
  } else {
    const lastDot = normalised.lastIndexOf('.')

    if (lastDot !== -1) {
      const decimalDigits = normalised.length - lastDot - 1

      normalised =
        decimalDigits >= 1 && decimalDigits <= 2
          ? `${normalised.slice(0, lastDot).replace(/\./g, '')}.${normalised.slice(lastDot + 1)}`
          : normalised.replace(/\./g, '')
    }
  }

  const parsed = Number(normalised)

  return Number.isFinite(parsed) ? parsed : null
}

/**
 * En dato i brugerens egen tidszone som "yyyy-MM-dd" — til de rene datofelter,
 * API'et tager imod.
 *
 * `toISOString().slice(0, 10)` giver UTC-datoen, og den er mellem midnat og
 * kl. 02 dansk sommertid stadig gårsdagens. En milepæl, der markeres som nået
 * "i dag", skal følge kalenderen på væggen, ikke Greenwich.
 */
export function toLocalIsoDate(value: Date = new Date()): string {
  return format(value, 'yyyy-MM-dd')
}

/** Initialer til avatarer: "Mikkel Hansen" → "MH". */
export function toInitials(fullName: string): string {
  const parts = fullName.trim().split(/\s+/).filter(Boolean)
  if (parts.length === 0) return '?'

  const first = parts[0]?.[0] ?? ''
  const last = parts.length > 1 ? (parts[parts.length - 1]?.[0] ?? '') : ''

  return (first + last).toUpperCase()
}
